/**
 * Campaign Management — admin-friendly redesign.
 * No developer concepts (IDs, cron, trigger types, campaign types).
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, IconButton, TextField, MenuItem, Chip,
  Alert, CircularProgress, InputAdornment, Stack, Tooltip, LinearProgress,
  Menu, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import {
  Add, EditOutlined, DeleteOutline, PlayCircleOutline, PauseCircleOutline,
  Search, SendOutlined, InsightsOutlined, GroupsOutlined, MoreVert, ScheduleOutlined,
  HistoryOutlined, CancelOutlined, ArrowBack,
} from '@mui/icons-material';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import CampaignWalletEstimate, { isWalletShort, type CampaignWalletPreview } from '@/components/billing/wallet/CampaignWalletEstimate';
import { getAuthHeaders } from '@/utils/api-headers.util';
import apiService from '@/services/api.service';
import { usePlanGate } from '@/hooks/usePlanGate';
import { showPlanLockFromErrorBody } from '@/utils/plan-lock.util';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Campaign {
  _id: string;
  name: string;
  description?: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  type: 'automated' | 'bulk' | 'event-driven';
  trigger: {
    type: 'newContact' | 'inactivity' | 'firstMessage' | 'conversationResolved' | 'followUp' | 'manual';
    delay: number;
    conditions: Array<{ field: string; operator: string; value: any }>;
    agentId?: string;
  };
  targeting: {
    filterType: 'all' | 'segment' | 'agent' | 'tag' | 'country' | 'contacts';
    filterValue?: string | string[] | SegmentFilter;
    excludeTags?: string[];
  };
  messageTemplate: {
    mode?: 'text' | 'template';
    templateText: string;
    templateName?: string;
    templateLanguage?: string;
    variables: string[];
  };
  schedule: {
    type: 'immediate' | 'scheduled' | 'recurring';
    startDate?: string;
    endDate?: string;
    cronExpression?: string;
  };
  createdAt: string;
  /** Estimated number of contacts this campaign would reach on its next send. */
  reach?: number;
  /**
   * Status of the referenced WhatsApp template as stored in the DB.
   * `null` means the template was not found (deleted). `undefined` means
   * this campaign is not in template mode.
   */
  templateStatus?: string | null;
  /**
   * Ground-truth sent/delivered/failed for this campaign's most recent broadcast,
   * computed server-side from that run's actual recipient logs — not `stats`
   * (a lifetime counter accumulated across every run, which can also drift; see
   * the backend's campaign.service.ts for why). Undefined if it has never run.
   */
  latestRun?: {
    _id: string;
    status: 'in_progress' | 'completed' | 'partial' | 'failed';
    startedAt: string;
    completedAt?: string;
    totals: { targeted: number; sent: number; delivered: number; failed: number; skipped: number };
  };
}

// Live progress for the most recent manual broadcast (`GET /:id/stats`'s `currentRun`).
interface CurrentRun {
  total: number;
  sent: number;
  delivered: number;
  failed: number;
  pending: number;
  skipped: number;
  optOut: number;
  startedAt: string;
  completedAt: string | null;
  inProgress: boolean;
}

interface SegmentFilter {
  tags?: string[];
  countryCodes?: string[];
  status?: string;
  agent?: string;
}

/**
 * One row of `GET /admin/campaigns/scheduled` — queue-truth for a
 * scheduled/recurring campaign, sourced from Bull/Redis directly rather than
 * only Mongo's `schedule` field (which can drift from what's actually
 * enqueued). See campaign.service.ts#getScheduledCampaigns.
 */
interface ScheduledJob {
  _id: string;
  name: string;
  scheduleType: string;
  cronExpression?: string;
  nextRunAt?: string;
  lastRunAt?: string;
  runCount: number;
  maxRuns?: number;
  jobId?: string;
  queueStatus: 'live' | 'orphaned' | 'no_job_id';
  queueState?: string;
  queueNextFireAt?: string;
  queueAttemptsMade?: number;
  queueFailedReason?: string;
}

/** One row of `GET /admin/campaigns/:id/schedule-history` — a permanent queue-lifecycle event. */
interface ScheduleHistoryEvent {
  _id: string;
  eventType:
    | 'enqueued' | 'requeued' | 'rescheduled'
    | 'cancelled_manual' | 'cancelled_delete' | 'cancelled_edit' | 'auto_paused' | 'completed';
  jobId?: string;
  previousJobId?: string;
  occurrenceAt?: string;
  actorId?: string;
  reason?: string;
  createdAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getRunsLabel(c: Campaign): string {
  if (c.trigger.type === 'inactivity')
    return `After ${c.trigger.delay}d inactivity`;
  if (c.trigger.type === 'followUp') {
    // trigger.delay is stored in MINUTES — show minutes under an hour, else hours,
    // so a "no reply after 2 minutes" follow-up no longer reads as "After 0h".
    const mins = c.trigger.delay;
    const label = mins < 60 ? `${mins}min` : `${Math.round(mins / 60)}h`;
    return `Follow-up · after ${label}`;
  }
  if (c.schedule.type === 'recurring' && c.schedule.cronExpression)
    return 'Recurring schedule';
  if (c.schedule.type === 'scheduled' && c.schedule.startDate)
    return `Scheduled · ${new Date(c.schedule.startDate).toLocaleDateString()}`;
  if (c.type === 'event-driven') return 'Via Workflow';
  return 'Manual';
}

function getStatusColor(s: string): 'success' | 'warning' | 'info' | 'default' {
  if (s === 'active')    return 'success';
  if (s === 'paused')    return 'warning';
  if (s === 'completed') return 'info';
  return 'default';
}

/**
 * Returns true when a template-mode campaign's template is no longer usable
 * (PAUSED / REJECTED / DISABLED by Meta, or deleted from the DB entirely).
 * Text-mode campaigns are never blocked here.
 */
function isTemplateBlocked(c: Campaign): boolean {
  return (
    c.messageTemplate.mode === 'template' &&
    !!c.messageTemplate.templateName &&
    c.templateStatus !== 'APPROVED'
  );
}

function templateBlockReason(c: Campaign): string {
  if (c.templateStatus === null) {
    return `Template "${c.messageTemplate.templateName}" was deleted — update this campaign to use another template`;
  }
  return `Template "${c.messageTemplate.templateName}" is ${(c.templateStatus || '').toLowerCase()} — sync from Meta or update this campaign`;
}

/** Soft, pill-shaped status indicator — dot + capitalized label on a tinted background. */
function StatusPill({ status }: { status: string }) {
  const color = getStatusColor(status);
  return (
    <Box
      sx={(theme: Theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.4,
        borderRadius: 5,
        bgcolor: color === 'default' ? theme.palette.grey[100] : alpha(theme.palette[color].main, 0.12),
        color: color === 'default' ? theme.palette.text.secondary : theme.palette[color].dark,
      })}
    >
      <Box
        sx={(theme: Theme) => ({
          width: 7,
          height: 7,
          borderRadius: '50%',
          flexShrink: 0,
          bgcolor: color === 'default' ? theme.palette.grey[400] : theme.palette[color].main,
        })}
      />
      <Typography variant="caption" fontWeight={600} sx={{ textTransform: 'capitalize', lineHeight: 1 }}>
        {status}
      </Typography>
    </Box>
  );
}

/** "in 3h 12m" / "in 5m" / "overdue" — coarse, human relative time for a future timestamp. */
function relativeTimeUntil(iso?: string): string {
  if (!iso) return '—';
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms)) return '—';
  if (ms <= 0) return 'due now';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `in ${mins}m`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours < 48) return `in ${hours}h${remMins ? ` ${remMins}m` : ''}`;
  return `in ${Math.floor(hours / 24)}d`;
}

/** Queue-truth status pill — distinct palette from StatusPill so it reads as "queue health", not campaign status. */
function QueueStatusPill({ status }: { status: ScheduledJob['queueStatus'] }) {
  const config = {
    live: { label: 'In queue', color: 'success' as const },
    orphaned: { label: 'Orphaned — needs re-save', color: 'error' as const },
    no_job_id: { label: 'Not enqueued', color: 'warning' as const },
  }[status];
  return (
    <Chip
      size="small"
      label={config.label}
      color={config.color}
      variant="outlined"
      sx={{ height: 22, fontSize: '0.7rem' }}
    />
  );
}

const SCHEDULE_EVENT_LABELS: Record<ScheduleHistoryEvent['eventType'], string> = {
  enqueued: 'Scheduled',
  requeued: 'Next occurrence queued',
  rescheduled: 'Rescheduled',
  cancelled_manual: 'Cancelled by admin',
  cancelled_delete: 'Cancelled — campaign deleted',
  cancelled_edit: 'Cancelled — schedule turned off',
  auto_paused: 'Auto-paused — template media needs re-upload',
  completed: 'Finished — nothing left to schedule',
};

/** Action button with a soft tinted background — replaces plain icon buttons for a less "cheap" look. */
function ActionIconButton({
  title, icon, color = 'default', onClick, disabled, loading,
}: {
  title: string;
  icon: React.ReactNode;
  color?: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info';
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  loading?: boolean;
}) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          disabled={disabled ?? false}
          onClick={onClick}
          sx={(theme: Theme) => {
            const main = color === 'default' ? theme.palette.text.secondary : theme.palette[color].main;
            return {
              mx: 0.3,
              borderRadius: 1.5,
              color: main,
              bgcolor: alpha(main, 0.08),
              '&:hover': { bgcolor: alpha(main, 0.18) },
              '&.Mui-disabled': { bgcolor: alpha(theme.palette.text.secondary, 0.04) },
            };
          }}
        >
          {loading ? <CircularProgress size={16} /> : icon}
        </IconButton>
      </span>
    </Tooltip>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Rows per request — matches the backend's own default page size. */
const PAGE_SIZE = 20;

const CampaignList: React.FC = () => {
  const apiBase = (import.meta.env.VITE_API_URL || '/api/v1').replace(/\/$/, '');
  const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });
  const navigate = useNavigate();
  const { guard } = usePlanGate();

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [search,    setSearch]    = useState('');
  // Server-side search, debounced so typing doesn't fire a request per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [page,      setPage]      = useState(0); // 0-indexed for TablePagination
  const [total,     setTotal]     = useState(0);
  const [whatsAppOk, setWhatsAppOk]     = useState<boolean | null>(null);

  // Confirm
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmCtx,  setConfirmCtx]  = useState<{
    action: 'delete' | 'trigger' | 'status' | 'cancelScheduledJob';
    id: string;
    value?: string;
    preview?: {
      totalContacts: number;
      outsideWindowCount: number;
      deactivatedCount: number;
      mode: 'text' | 'template';
      /** Prepaid wallet estimate; null when the wallet doesn't meter this workspace. */
      wallet?: CampaignWalletPreview | null;
    };
  } | null>(null);
  const [previewLoadingId, setPreviewLoadingId] = useState('');

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuCampaignId, setMenuCampaignId] = useState<string | null>(null);

  // Scheduled Jobs dialog — live Bull-queue truth, fetched on open (see
  // GET /admin/campaigns/scheduled).
  const [scheduledOpen, setScheduledOpen] = useState(false);
  const [scheduledLoading, setScheduledLoading] = useState(false);
  const [scheduledJobs, setScheduledJobs] = useState<ScheduledJob[]>([]);
  const [scheduledError, setScheduledError] = useState('');

  // History drill-down within the same dialog — set to a campaign id to show
  // its permanent CampaignScheduleEvent trail instead of the live list.
  const [historyCampaignId, setHistoryCampaignId] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyEvents, setHistoryEvents] = useState<ScheduleHistoryEvent[]>([]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, campaignId: string) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuCampaignId(campaignId);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuCampaignId(null);
  };

  // Live broadcast progress, keyed by campaign id (see `fetchRunStats`).
  const [runProgress, setRunProgress] = useState<Record<string, CurrentRun>>({});
  const pollRefs = useRef<Record<string, ReturnType<typeof setInterval>>>({});
  // When a run's `inProgress` first flips to false, we keep polling for a
  // short grace period — Meta's delivery-status webhooks (delivered/failed,
  // e.g. error 131049) often arrive a little after the synchronous send
  // completes, and would otherwise never be reflected in `runProgress`.
  const runCompletedAtRef = useRef<Record<string, number>>({});
  const POLL_GRACE_MS = 2 * 60 * 1000;

  // ── Load ────────────────────────────────────────────────────────────────────

  // Reads stay open even when campaignsEnabled is off (see campaign.routes.ts) so
  // this tenant's real campaign history still renders — only CTAs are gated
  // (see usePlanGate below), not the page itself.
  // `page` is reset to 0 by the search/status handlers themselves, so a filter
  // change and its page reset arrive in the same render — one fetch, not two.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0); // with the debounced value, so both land in one render
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => { fetchCampaigns(); }, [debouncedSearch, filterStatus, page]);
  useEffect(() => { fetchWhatsAppSetup(); }, []);

  // Stop all progress polling on unmount.
  useEffect(() => () => {
    Object.values(pollRefs.current).forEach(clearInterval);
    pollRefs.current = {};
  }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (debouncedSearch) p.append('search', debouncedSearch);
      if (filterStatus !== 'all') p.append('status', filterStatus);
      p.append('page', String(page + 1));
      p.append('limit', String(PAGE_SIZE));
      const r = await apiService.authorizedFetch(`${apiBase}/admin/campaigns?${p}`, { headers: authHdr() });
      const d = await r.json();
      if (d.success) {
        const list: Campaign[] = d.data || [];
        setCampaigns(list);
        setTotal(d.meta?.total ?? 0);
        // Resume polling for any campaign with a broadcast still in flight
        // (e.g. the admin refreshed the page mid-run).
        list.forEach((c) => {
          if (c.latestRun?.status === 'in_progress') startPolling(c._id);
        });
      } else setError(d.error?.message || 'Failed to load campaigns');
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };

  // ── Broadcast progress polling ──────────────────────────────────────────────

  const fetchRunStats = async (campaignId: string) => {
    try {
      const r = await apiService.authorizedFetch(`${apiBase}/admin/campaigns/${campaignId}/stats`, { headers: authHdr() });
      const d = await r.json();
      const run: CurrentRun | null = d.success ? d.data?.currentRun ?? null : null;
      if (!run) {
        stopPolling(campaignId);
        return;
      }
      setRunProgress((p) => ({ ...p, [campaignId]: run }));

      if (run.inProgress) {
        delete runCompletedAtRef.current[campaignId];
        return;
      }

      // Run just finished — keep polling for a grace period to catch
      // trailing delivery/failure webhooks before stopping.
      const completedAt = runCompletedAtRef.current[campaignId] ?? Date.now();
      runCompletedAtRef.current[campaignId] = completedAt;
      if (Date.now() - completedAt >= POLL_GRACE_MS) {
        delete runCompletedAtRef.current[campaignId];
        stopPolling(campaignId);
        fetchCampaigns();
      }
    } catch {
      // transient error — keep polling, next tick may succeed
    }
  };

  const startPolling = (campaignId: string) => {
    if (pollRefs.current[campaignId]) return;
    fetchRunStats(campaignId);
    pollRefs.current[campaignId] = setInterval(() => fetchRunStats(campaignId), 4000);
  };

  const stopPolling = (campaignId: string) => {
    const handle = pollRefs.current[campaignId];
    if (handle) {
      clearInterval(handle);
      delete pollRefs.current[campaignId];
    }
    delete runCompletedAtRef.current[campaignId];
  };

  // ── Scheduled Jobs (live Bull-queue truth) ──────────────────────────────────

  const openScheduledJobs = async () => {
    setScheduledOpen(true);
    setHistoryCampaignId(null);
    setScheduledLoading(true);
    setScheduledError('');
    try {
      const r = await apiService.authorizedFetch(`${apiBase}/admin/campaigns/scheduled`, { headers: authHdr() });
      const d = await r.json();
      if (d.success) setScheduledJobs(d.data?.scheduled || []);
      else setScheduledError(d.error?.message || 'Failed to load scheduled jobs');
    } catch (e: any) {
      setScheduledError(e.message);
    } finally {
      setScheduledLoading(false);
    }
  };

  const openScheduleHistory = async (campaignId: string) => {
    setHistoryCampaignId(campaignId);
    setHistoryLoading(true);
    try {
      const r = await apiService.authorizedFetch(`${apiBase}/admin/campaigns/${campaignId}/schedule-history`, { headers: authHdr() });
      const d = await r.json();
      setHistoryEvents(d.success ? d.data?.history || [] : []);
    } catch {
      setHistoryEvents([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleCancelScheduledJob = async (campaignId: string) => {
    try {
      const r = await apiService.authorizedFetch(`${apiBase}/admin/campaigns/${campaignId}/scheduled-job`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      const d = await r.json();
      if (d.success) {
        setSuccess('Scheduled job cancelled — campaign is now paused.');
        openScheduledJobs();
        fetchCampaigns();
      } else if (!showPlanLockFromErrorBody(d.error)) {
        setError(d.error?.message || 'Failed to cancel scheduled job');
      }
    } catch (e: any) {
      setError(e.message);
    }
  };

  const fetchWhatsAppSetup = async () => {
    try {
      const r = await apiService.authorizedFetch(`${apiBase}/whatsapp/config`, { headers: authHdr() });
      const d = await r.json();
      if (d.success) setWhatsAppOk(Boolean(d.data?.setupComplete));
    } catch { setWhatsAppOk(null); }
  };

  // ── Trigger preview (pre-flight check before "Send Now") ────────────────────

  const openTriggerConfirm = async (c: Campaign) => {
    setPreviewLoadingId(c._id);
    try {
      const r = await apiService.authorizedFetch(`${apiBase}/admin/campaigns/${c._id}/trigger-preview`, { headers: authHdr() });
      const d = await r.json();
      setConfirmCtx({ action: 'trigger', id: c._id, preview: d.success ? d.data : undefined });
    } catch {
      setConfirmCtx({ action: 'trigger', id: c._id });
    } finally {
      setPreviewLoadingId('');
      setConfirmOpen(true);
    }
  };

  // ── Confirm actions ──────────────────────────────────────────────────────────

  const handleConfirmAction = async () => {
    if (!confirmCtx) return;
    try {
      if (confirmCtx.action === 'delete') {
        const r = await apiService.authorizedFetch(`${apiBase}/admin/campaigns/${confirmCtx.id}`, { method: 'DELETE', headers: authHdr() });
        const d = await r.json();
        if (d.success) {
          setSuccess('Campaign deleted.');
          // Deleting the only row on a page would otherwise leave the reader
          // stranded on an empty one — step back instead (the page effect
          // refetches).
          if (campaigns.length === 1 && page > 0) setPage(page - 1);
          else fetchCampaigns();
        }
        else if (!showPlanLockFromErrorBody(d.error)) setError(d.error?.message || 'Failed to delete');
      } else if (confirmCtx.action === 'trigger') {
        const r = await apiService.authorizedFetch(`${apiBase}/admin/campaigns/${confirmCtx.id}/trigger`, { method: 'POST', headers: getAuthHeaders() });
        const d = await r.json();
        if (d.success) {
          const result = d.data || {};
          if (result.total > 1) {
            setSuccess(result.message || `Sending to ${result.total} contacts…`);
            startPolling(confirmCtx.id);
          } else {
            setSuccess(`Sent: ${result.sent ?? 0}  ·  Failed: ${result.failed ?? 0}  ·  Skipped: ${result.skipped ?? 0}`);
          }
          fetchCampaigns();
        } else if (!showPlanLockFromErrorBody(d.error)) setError(d.error?.message || 'Failed to send');
      } else if (confirmCtx.action === 'status') {
        const r = await apiService.authorizedFetch(`${apiBase}/admin/campaigns/${confirmCtx.id}`, {
          method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ status: confirmCtx.value }),
        });
        const d = await r.json();
        if (d.success) { setSuccess(`Campaign ${confirmCtx.value}.`); fetchCampaigns(); }
        else if (!showPlanLockFromErrorBody(d.error)) setError(d.error?.message || 'Failed');
      } else if (confirmCtx.action === 'cancelScheduledJob') {
        await handleCancelScheduledJob(confirmCtx.id);
      }
    } catch (e: any) { setError(e.message); }
    setConfirmOpen(false);
    setConfirmCtx(null);
  };

  // The wallet can't cover this send: the confirm button becomes "Top up wallet".
  const triggerWalletShort = confirmCtx?.action === 'trigger' && isWalletShort(confirmCtx.preview?.wallet);

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Campaigns</Typography>
          <Typography variant="body2" color="text.secondary">
            Create and send WhatsApp message campaigns to your contacts
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button variant="outlined" startIcon={<ScheduleOutlined />} onClick={openScheduledJobs}>
            Scheduled Jobs
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={guard('campaignsEnabled', () => navigate('/admin/campaigns/new'))}>
            New Campaign
          </Button>
        </Stack>
      </Box>

      {/* ── Alerts ──────────────────────────────────────────────────────────── */}
      {whatsAppOk === false && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          WhatsApp is not fully configured yet. Contact your account admin to complete setup before sending.
        </Alert>
      )}
      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* ── Filters ─────────────────────────────────────────────────────────── */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: '12px !important' }}>
          <Stack direction="row" spacing={2}>
            <TextField
              size="small" placeholder="Search campaigns…" value={search}
              onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            />
            <TextField size="small" select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }} sx={{ width: 160 }}>
              <MenuItem value="all">All statuses</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="paused">Paused</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </TextField>
          </Stack>
        </CardContent>
      </Card>

      {/* ── Table ───────────────────────────────────────────────────────────── */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : campaigns.length === 0 ? (
            <Box textAlign="center" py={8}>
              <SendOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary" mb={2}>No campaigns yet. Create your first one.</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={guard('campaignsEnabled', () => navigate('/admin/campaigns/new'))}>New Campaign</Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Campaign</TableCell>
                    <TableCell>Runs</TableCell>
                    <TableCell>Recipients</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sent / Delivered / Failed</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns
                    .filter((c) =>
                      (!search || c.name.toLowerCase().includes(search.toLowerCase()) || c.description?.toLowerCase().includes(search.toLowerCase())) &&
                      (filterStatus === 'all' || c.status === filterStatus)
                    )
                    .map((c) => (
                      <TableRow key={c._id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography
                              fontWeight={500}
                              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                              onClick={() => navigate(`/admin/campaigns/${c._id}`)}
                            >
                              {c.name}
                            </Typography>
                            {isTemplateBlocked(c) && (
                              <Tooltip title={templateBlockReason(c)}>
                                <Chip
                                  size="small"
                                  label={c.templateStatus === null ? 'Template deleted' : `Template ${(c.templateStatus || '').toLowerCase()}`}
                                  color="warning"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem', cursor: 'help' }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                          {c.description && <Typography variant="caption" color="text.secondary" display="block">{c.description}</Typography>}
                          {c.messageTemplate?.mode === 'template' && c.messageTemplate.templateName && (
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                Template: <strong>{c.messageTemplate.templateName}</strong>
                              </Typography>
                              {!isTemplateBlocked(c) && c.templateStatus === 'APPROVED' && (
                                <Chip
                                  size="small"
                                  label="APPROVED"
                                  color="success"
                                  variant="outlined"
                                  sx={{ height: 16, fontSize: '0.6rem' }}
                                />
                              )}
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell>
                          {/* Behavior triggers (follow-up / inactivity) get a solid
                              navy chip so they stand out from manual/scheduled
                              broadcasts — the "segregation" of automated nudges. */}
                          {(() => {
                            const isBehavior =
                              c.trigger.type === 'followUp' || c.trigger.type === 'inactivity';
                            return (
                              <Chip
                                size="small"
                                label={getRunsLabel(c)}
                                variant="outlined"
                                sx={
                                  isBehavior
                                    ? {
                                        bgcolor: '#1e3a8a',
                                        color: '#fff',
                                        borderColor: '#1e3a8a',
                                        fontWeight: 600,
                                        '& .MuiChip-label': { color: '#fff' },
                                      }
                                    : {}
                                }
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {c.targeting.filterType === 'all' ? 'Everyone' :
                             c.targeting.filterType === 'agent' ? 'By agent' :
                             c.targeting.filterType === 'tag' ? 'By tag' :
                             c.targeting.filterType === 'country' ? 'By country' :
                             c.targeting.filterType === 'contacts' ? 'Specific contacts' :
                             c.targeting.filterType === 'segment' ? 'Custom segment' : c.targeting.filterType}
                          </Typography>
                          {c.targeting.filterType === 'tag' && c.targeting.filterValue && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, wordBreak: 'break-all' }}>
                              {Array.isArray(c.targeting.filterValue) ? c.targeting.filterValue.join(', ') : String(c.targeting.filterValue)}
                            </Typography>
                          )}
                          {typeof c.reach === 'number' && (
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                              <GroupsOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {c.reach.toLocaleString()} contact{c.reach === 1 ? '' : 's'}
                              </Typography>
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusPill status={c.status} />
                        </TableCell>
                        <TableCell>
                          {c.latestRun ? (
                            <>
                              <Typography variant="body2">{c.latestRun.totals.sent} sent</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {c.latestRun.totals.delivered} delivered
                              </Typography>
                              {c.latestRun.totals.failed > 0 && (
                                <Typography variant="caption" color="error.main" display="block">
                                  {c.latestRun.totals.failed} failed
                                </Typography>
                              )}
                            </>
                          ) : (
                            <Typography variant="caption" color="text.secondary">Not sent yet</Typography>
                          )}
                          {(() => {
                            const run = runProgress[c._id];
                            if (!run) return null;
                            const processed = run.sent + run.delivered + run.failed + run.skipped + run.optOut;
                            const pct = run.total > 0 ? Math.min(100, (processed / run.total) * 100) : 0;
                            return (
                              <Box sx={{ mt: 0.75, minWidth: 120 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={pct}
                                  color={run.inProgress ? 'primary' : run.failed > 0 ? 'warning' : 'success'}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {run.inProgress
                                    ? `Sending… ${processed} / ${run.total}`
                                    : `Done · ${processed} / ${run.total}`}
                                </Typography>
                              </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <ActionIconButton
                              title={
                                isTemplateBlocked(c)
                                  ? templateBlockReason(c)
                                  : c.status !== 'active'
                                    ? 'Activate campaign first'
                                    : 'Send now to all matching contacts'
                              }
                              icon={<SendOutlined fontSize="small" />}
                              color="primary"
                              disabled={c.status !== 'active' || isTemplateBlocked(c) || previewLoadingId === c._id}
                              loading={previewLoadingId === c._id}
                              onClick={guard('campaignsEnabled', () => openTriggerConfirm(c))}
                            />
                            <ActionIconButton
                              title="Edit campaign"
                              icon={<EditOutlined fontSize="small" />}
                              onClick={guard('campaignsEnabled', () => navigate(`/admin/campaigns/${c._id}/edit`))}
                            />
                            <ActionIconButton
                              title="More options"
                              icon={<MoreVert fontSize="small" />}
                              onClick={(e) => handleMenuOpen(e, c._id)}
                            />
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!loading && total > 0 && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
            />
          )}
        </CardContent>
      </Card>

      {/* ── More Options Menu ─────────────────────────────────────────────────── */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl) && menuCampaignId !== null}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { sx: { minWidth: 160, mt: 0.5, borderRadius: 2 } } }}
      >
        {(() => {
          const c = campaigns.find(x => x._id === menuCampaignId);
          if (!c) return null;
          return [
            <MenuItem key="analytics" onClick={() => { handleMenuClose(); navigate(`/admin/campaigns/${c._id}`); }}>
              <ListItemIcon><InsightsOutlined fontSize="small" color="info" /></ListItemIcon>
              <ListItemText>Analytics & Logs</ListItemText>
            </MenuItem>,
            c.status === 'active' ? (
              <MenuItem key="pause" onClick={guard('campaignsEnabled', () => { handleMenuClose(); setConfirmCtx({ action: 'status', id: c._id, value: 'paused' }); setConfirmOpen(true); })}>
                <ListItemIcon><PauseCircleOutline fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText>Pause</ListItemText>
              </MenuItem>
            ) : (c.status === 'paused' || c.status === 'draft') ? (
              <MenuItem
                key="activate"
                disabled={isTemplateBlocked(c)}
                onClick={guard('campaignsEnabled', () => { handleMenuClose(); setConfirmCtx({ action: 'status', id: c._id, value: 'active' }); setConfirmOpen(true); })}
              >
                <ListItemIcon><PlayCircleOutline fontSize="small" color={isTemplateBlocked(c) ? 'action' : 'success'} /></ListItemIcon>
                <ListItemText>Activate</ListItemText>
              </MenuItem>
            ) : null,
            <MenuItem key="delete" onClick={() => { handleMenuClose(); setConfirmCtx({ action: 'delete', id: c._id }); setConfirmOpen(true); }} sx={{ color: 'error.main' }}>
              <ListItemIcon><DeleteOutline fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          ];
        })()}
      </Menu>

      {/* ── Scheduled Jobs dialog (live Bull-queue truth, per tenant) ─────────── */}
      <Dialog open={scheduledOpen} onClose={() => setScheduledOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {historyCampaignId ? (
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton size="small" onClick={() => setHistoryCampaignId(null)}><ArrowBack fontSize="small" /></IconButton>
              Schedule History — {scheduledJobs.find((j) => j._id === historyCampaignId)?.name || ''}
            </Box>
          ) : 'Scheduled Jobs'}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
            {historyCampaignId
              ? 'Permanent record of every enqueue, reschedule, and cancellation for this campaign — survives even if the job itself is long gone from the queue.'
              : "Live queue state for every scheduled or recurring campaign — sourced from the send queue itself, not just what's saved."}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {historyCampaignId ? (
            historyLoading ? (
              <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
            ) : historyEvents.length === 0 ? (
              <Box textAlign="center" py={6}>
                <HistoryOutlined sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">No schedule history recorded yet.</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell>Detail</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyEvents.map((e) => (
                      <TableRow key={e._id} hover>
                        <TableCell>
                          <Typography variant="caption">{new Date(e.createdAt).toLocaleString()}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{SCHEDULE_EVENT_LABELS[e.eventType]}</Typography>
                        </TableCell>
                        <TableCell>
                          {e.occurrenceAt && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Fire time: {new Date(e.occurrenceAt).toLocaleString()}
                            </Typography>
                          )}
                          {e.reason && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {e.reason}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : (
            <>
              {scheduledError && <Alert severity="error" sx={{ mb: 2 }}>{scheduledError}</Alert>}
              {scheduledLoading ? (
                <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
              ) : scheduledJobs.length === 0 ? (
                <Box textAlign="center" py={6}>
                  <ScheduleOutlined sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">No scheduled or recurring campaigns right now.</Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Campaign</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Next fire</TableCell>
                        <TableCell>Queue</TableCell>
                        <TableCell>Runs</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scheduledJobs.map((j) => (
                        <TableRow key={j._id} hover>
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                              onClick={() => { setScheduledOpen(false); navigate(`/admin/campaigns/${j._id}`); }}
                            >
                              {j.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                              {j.scheduleType}
                            </Typography>
                            {j.cronExpression && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {j.cronExpression}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {relativeTimeUntil(j.queueNextFireAt || j.nextRunAt)}
                            </Typography>
                            {j.queueNextFireAt && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {new Date(j.queueNextFireAt).toLocaleString()}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <QueueStatusPill status={j.queueStatus} />
                            {j.queueStatus === 'live' && (j.queueAttemptsMade ?? 0) > 0 && (
                              <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.25 }}>
                                {j.queueAttemptsMade} attempt{j.queueAttemptsMade === 1 ? '' : 's'} made
                              </Typography>
                            )}
                            {j.queueFailedReason && (
                              <Tooltip title={j.queueFailedReason}>
                                <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.25, cursor: 'help' }}>
                                  Last error ⓘ
                                </Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {j.runCount}{j.maxRuns ? ` / ${j.maxRuns}` : ''}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <ActionIconButton
                                title="View schedule history"
                                icon={<HistoryOutlined fontSize="small" />}
                                onClick={() => openScheduleHistory(j._id)}
                              />
                              {j.queueStatus !== 'no_job_id' && (
                                <ActionIconButton
                                  title="Cancel this scheduled job — the campaign will be paused"
                                  icon={<CancelOutlined fontSize="small" />}
                                  color="error"
                                  onClick={() => { setConfirmCtx({ action: 'cancelScheduledJob', id: j._id }); setConfirmOpen(true); }}
                                />
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!historyCampaignId && <Button onClick={openScheduledJobs} disabled={scheduledLoading}>Refresh</Button>}
          <Button onClick={() => setScheduledOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Confirm dialog ─────────────────────────────────────────────────── */}
      <ConfirmDialog
        open={confirmOpen}
        title={
          confirmCtx?.action === 'delete'             ? 'Delete Campaign' :
          confirmCtx?.action === 'trigger'             ? 'Send Campaign Now' :
          confirmCtx?.action === 'cancelScheduledJob'  ? 'Cancel Scheduled Job' :
          confirmCtx?.value === 'paused'                ? 'Pause Campaign' : 'Activate Campaign'
        }
        content={
          confirmCtx?.action === 'delete' ? 'This will permanently delete the campaign. This cannot be undone.' :
          confirmCtx?.action === 'cancelScheduledJob'
            ? 'This permanently removes the queued job — it will not fire, and the campaign will be paused. You can re-activate it later to schedule a new occurrence.'
          : confirmCtx?.action === 'trigger' ? (
            <Box>
              <Typography variant="body2" color="text.secondary">
                This will immediately send the message to{' '}
                {confirmCtx.preview ? `${confirmCtx.preview.totalContacts} matching contact${confirmCtx.preview.totalContacts === 1 ? '' : 's'}` : 'all matching contacts'}. Are you sure?
              </Typography>
              {confirmCtx.preview && confirmCtx.preview.mode === 'text' && confirmCtx.preview.outsideWindowCount > 0 && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  {confirmCtx.preview.outsideWindowCount} of {confirmCtx.preview.totalContacts} recipient
                  {confirmCtx.preview.outsideWindowCount === 1 ? '' : 's'} haven't messaged you in the last 24 hours.
                  WhatsApp will reject free-text messages to {confirmCtx.preview.outsideWindowCount === 1 ? 'them' : 'these contacts'} —
                  switch this campaign's message type to an approved WhatsApp template to reach everyone.
                </Alert>
              )}
              {confirmCtx.preview && confirmCtx.preview.deactivatedCount > 0 && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  {confirmCtx.preview.deactivatedCount} contact{confirmCtx.preview.deactivatedCount === 1 ? '' : 's'} matching
                  this campaign {confirmCtx.preview.deactivatedCount === 1 ? 'is' : 'are'} deactivated and will be skipped —
                  see the run log for details.
                </Alert>
              )}
              <CampaignWalletEstimate wallet={confirmCtx.preview?.wallet} />
            </Box>
          ) :
          confirmCtx?.value === 'paused' ? 'The campaign will stop sending until you activate it again.' :
          'The campaign will become active and start sending based on its schedule.'
        }
        confirmText={
          confirmCtx?.action === 'delete'            ? 'Delete' :
          confirmCtx?.action === 'trigger'            ? (triggerWalletShort ? 'Top up wallet' : 'Send Now') :
          confirmCtx?.action === 'cancelScheduledJob' ? 'Cancel Job' : 'Confirm'
        }
        cancelText="Cancel"
        {...(confirmCtx?.action === 'delete' || confirmCtx?.action === 'cancelScheduledJob' ? { severity: 'error' as const } : {})}
        {...(confirmCtx?.action === 'trigger' && confirmCtx.preview?.mode === 'text' && confirmCtx.preview.outsideWindowCount > 0 ? { severity: 'warning' as const } : {})}
        {...(triggerWalletShort ? { severity: 'error' as const } : {})}
        onConfirm={
          triggerWalletShort
            ? () => { setConfirmOpen(false); setConfirmCtx(null); navigate('/admin/billing/wallet'); }
            : handleConfirmAction
        }
        onCancel={() => { setConfirmOpen(false); setConfirmCtx(null); }}
      />
    </Box>
  );
};

export default CampaignList;
